import React, { useState, useEffect, useRef } from "react";
import "./App.css";

function App() {
  const [message, setMessage] = useState("");
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true); // Chatbot always open
  const [paused, setPaused] = useState(false);

  // 👤 Name screen
  const [userName, setUserName] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [started, setStarted] = useState(false);

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState("");
  // 🌐 BASE URL FOR IIS / LOCALHOST
  const API_BASE = `${window.location.origin}/api`;
  const chatbotToken = new URLSearchParams(window.location.search).get("token");
  const msgEndRef = useRef(null);

  // -----------------------------
  // 🎤 VOICE SEARCH SETUP
  // -----------------------------
  // -----------------------------
  // 🎤 VOICE SEARCH SETUP
  // -----------------------------
  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;

  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const speechRef = useRef(null);
useEffect(() => {
  if ("speechSynthesis" in window) {
    window.speechSynthesis.onvoiceschanged = () => {
      window.speechSynthesis.getVoices();
    };
  }
}, []);
  useEffect(() => {
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN"; // handles English + Marathi reasonably
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setListening(true);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognition.onerror = (event) => {
      console.error("Voice recognition error:", event.error);
      setListening(false);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.trim();
      if (transcript) {
        sendVoiceMessage(transcript);
      }
    };

    recognitionRef.current = recognition;
  }, []);

  // ===============================
  // 🔊 VOICE / TEXT TO SPEECH
  // ===============================

  const stopVoice = () => {
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }

    speechRef.current = null;
  };

  const speakText = (text) => {
    if (!voiceEnabled) return;

    if (!("speechSynthesis" in window)) {
      console.warn("Speech synthesis is not supported in this browser.");
      return;
    }

    if (!text || !text.trim()) return;

    // Cancel any active speech
    window.speechSynthesis.cancel();

    // 1. Remove badges/remarks and special characters
    const cleanSpeechText = text
      .replace(/📘.*|🌐.*|⚠️.*/g, "")
      .replace(/[*#_`]/g, "")
      .replace(/https?:\/\/\S+/g, "")
      .replace(/[🔊🔔⭐👋🙏📌✅❌⏳]/g, "")
      .replace(/\s+/g, " ")
      .trim();

    if (!cleanSpeechText) return;

    const utterance = new SpeechSynthesisUtterance(cleanSpeechText);
    const hasMarathi = isMarathi(cleanSpeechText);

    // 2. Pick the best available voice on the device
    const availableVoices = window.speechSynthesis.getVoices();

    if (hasMarathi) {
      utterance.lang = "mr-IN";
      // Search for dedicated Marathi voice (Google मराठी, Microsoft Hemant/Kalpana, etc.)
      const marathiVoice = availableVoices.find(
        (v) => v.lang === "mr-IN" || v.lang === "mr_IN" || v.name.toLowerCase().includes("marathi")
      );

      if (marathiVoice) {
        utterance.voice = marathiVoice;
      } else {
        // Fallback to Hindi voice if device lacks Marathi pack (renders Devanagari phonemes accurately)
        const hindiVoice = availableVoices.find(
          (v) => v.lang === "hi-IN" || v.name.toLowerCase().includes("hindi")
        );
        if (hindiVoice) utterance.voice = hindiVoice;
      }
      utterance.rate = 0.90; // Slightly slower pace for clear Marathi pronunciation
    } else {
      utterance.lang = "en-IN";
      const indianEnVoice = availableVoices.find(
        (v) => v.lang === "en-IN" || v.name.toLowerCase().includes("india")
      );
      if (indianEnVoice) utterance.voice = indianEnVoice;
      utterance.rate = 0.95;
    }

    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    utterance.onstart = () => {
      speechRef.current = utterance;
    };

    utterance.onend = () => {
      speechRef.current = null;
    };

    utterance.onerror = (event) => {
      console.warn("Speech synthesis error:", event);
      speechRef.current = null;
    };

    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const toggleVoice = () => {
    if (voiceEnabled) {
      // Turning voice OFF
      stopVoice();
      setVoiceEnabled(false);
    } else {
      // Turning voice ON
      setVoiceEnabled(true);
    }
  };

  const sendVoiceMessage = async (voiceText) => {
    setCategories([]);
    setSchemes([]);
    setSelectedCategory("");
    setSelectedScheme("");

    // 👤 Add as user message
    setChat((prev) => [...prev, { role: "user", text: voiceText }]);
    setLoading(true);

    try {
      // const res = await fetch("http://127.0.0.1:5000/chat", {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: voiceText,
          chatbot_token: chatbotToken,
        }),
      });

      const data = await res.json();
      typeMessage((data.answer || "").replace(/\\n/g, "\n"));
    } catch {
      typeMessage("Server error. Try again.");
    }

    setLoading(false);
  };

  // Detect whether text is Marathi (Devanagari script)
  const isMarathi = (text) => {
    const devanagari = /[\u0900-\u097F]/;
    return devanagari.test(text);
  };

  // // 🔥 AUTO GREETING — runs ONLY ON FIRST LOAD
  // useEffect(() => {
  //   setChat([
  //     {
  //       role: "bot",
  //       type: "greeting",
  //       text:
  //         "👋 Welcome to MEDA Support!\n" +
  //         "How can I assist you today?\n\n" +
  //         "Please type your query below.\n" +
  //         "आपला प्रश्न खालील टेक्स्ट बॉक्समध्ये लिहा."
  //     }
  //   ]);
  // }, []);

  const startChat = () => {
  const name = nameInput.trim();

  if (!name) {
    return;
  }

  setUserName(name);
  setStarted(true);

  const greeting = `👋 नमस्कार ${name}!\nWelcome to MEDA Support.`;

  setChat([
    {
      role: "bot",
      type: "personal-greeting",
      text: greeting,
      complete: true,
      translated: "",
      translating: false,
    },
    {
      role: "bot",
      type: "normal",
      text: "How can I assist you today?",
      complete: true,
      translated: "",
      translating: false,
    },
  ]);

  speakText(`${greeting} How can I assist you today?`);
};

  const resetChat = () => {
    stopVoice();

    setCategories([]);
    setSchemes([]);
    setSelectedCategory("");
    setSelectedScheme("");

    setChat([
      {
        role: "bot",
        type: "greeting",
        text:
          "👋 Welcome to MEDA Support!\n\n" +
          "How can I assist you today?\n\n" +
          "Please type your query below.\n" +
          "आपला प्रश्न खालील टेक्स्ट बॉक्समध्ये लिहा.",
        complete: true,
        translated: "",
        translating: false,
      },
    ]);
  };
  // Auto-scroll
  useEffect(() => {
    if (msgEndRef.current) {
      msgEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chat, loading]);

  const extractRemarksForUI = (text) => {
    if (!text) return { remark: "", cleanText: "" };

    const sourceKeywords = [
      "fetched from the web",
      "answer sourced from the web",
      "not found in database",
      "not from the meda database",
      "based on meda database",
      "specific data not available",
    ];

    const lines = text.split("\n");
    const remarkLines = [];
    const answerLines = [];

    lines.forEach((line) => {
      const lower = line.toLowerCase();
      if (sourceKeywords.some((k) => lower.includes(k))) {
        remarkLines.push(line.trim());
      } else {
        answerLines.push(line);
      }
    });

    return {
      remark: remarkLines.join(" • "), // 🔑 ONE combined remark
      cleanText: answerLines.join("\n").trim(),
    };
  };

  // FETCH SCHEMES
  const fetchSchemes = (category) => {
    setSelectedCategory(category);
    // fetch(`http://127.0.0.1:5000/get_schemes?category=${category}`)
    fetch(`${API_BASE}/get_schemes?category=${encodeURIComponent(category)}`)
      .then((res) => res.json())
      .then((data) => setSchemes(data.schemes))
      .catch(console.error);
  };

  // FETCH SCHEME DETAILS

  const fetchSchemeDetails = (scheme) => {
    setSelectedScheme(scheme);
    setSchemes([]);

    setChat((prev) => [
      ...prev,
      { role: "user", text: `Tell me about ${scheme}` },
    ]);

    // fetch("http://127.0.0.1:5000/scheme_details", {
    fetch(`${API_BASE}/scheme_details`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scheme, chatbot_token: chatbotToken }),
    })
      .then((res) => res.json())
      .then((data) => {
        typeMessage((data.answer || "").replace(/\\n/g, "\n"));
      })
      .catch(() => {
        typeMessage("Error fetching scheme details.");
      });
  };

  const typeMessage = (text) => {
    const { remark, cleanText } = extractRemarksForUI(text);

    let idx = 0;

    setChat((prev) => [
      ...prev,
      {
        role: "bot",
        remark,
        text: "",
        complete: false,
        translated: "",
        translating: false,
      },
    ]);

    const interval = setInterval(() => {
      if (idx < cleanText.length) {
        setChat((prev) => {
          const updated = [...prev];

          if (updated.length > 0) {
            updated[updated.length - 1].text = cleanText.slice(0, idx + 1);
          }

          return updated;
        });

        idx++;
      } else {
        clearInterval(interval);

        setChat((prev) => {
          const updated = [...prev];

          if (updated.length > 0) {
            updated[updated.length - 1].complete = true;
          }

          return updated;
        });

        // 🔊 Speak the complete answer
        speakText(cleanText);
      }
    }, 15);
  };

  const translateMessage = async (text, index) => {
    try {
      setChat((prev) => {
        const updated = [...prev];
        updated[index].translating = true;
        return updated;
      });

      const targetLang = isMarathi(text) ? "english" : "marathi";

      // const res = await fetch("http://127.0.0.1:5000/translate", {
      const res = await fetch(`${API_BASE}/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
          target: targetLang,
          chatbot_token: chatbotToken,
        }),
      });

      const data = await res.json();

      setChat((prev) => {
        const updated = [...prev];
        updated[index].translated = data.translated;
        updated[index].translating = false;
        return updated;
      });
    } catch (e) {
      alert("Translation error: " + e.message);
    }
  };

  // SEND MESSAGE
  const sendMessage = async () => {
    if (!message.trim()) return;

    setCategories([]);
    setSchemes([]);
    setSelectedCategory("");
    setSelectedScheme("");

    const userMsg = message;
    setMessage("");

    setChat((prev) => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    try {
      // const res = await fetch("http://127.0.0.1:5000/chat", {
      const res = await fetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, chatbot_token: chatbotToken }),
      });

      const data = await res.json();
      typeMessage((data.answer || "").replace(/\\n/g, "\n"));

      // , () => {
      // //   if (!data.skip_satisfaction) {
      // //     setChat(prev => [
      // //       ...prev,
      // //       {
      // //         role: "bot",
      // //         text: "Are you satisfied with this answer?",
      // //         complete: true,
      // //         askSatisfaction: true
      // //     }
      // //   ]);
      // // }
      // // });
    } catch {
      typeMessage("Server error. Try again.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="chat-container">
        {/* HEADER */}
        <div className="chat-header">
          <span>MEDA Support (BETA)</span>

          <div className="header-actions">
            <button
              type="button"
              className="voice-toggle-btn"
              onClick={toggleVoice}
            >
              {voiceEnabled ? "🔊 Voice On" : "🔇 Voice Off"}
            </button>

            <button type="button" className="reset-btn" onClick={resetChat}>
              Reset
            </button>
          </div>
        </div>

{!started ? (
  // ============================
  // 👤 NAME ENTRY SCREEN
  // ============================
  <div className="name-screen">

    <div className="name-logo">
      <img src="bot.png" alt="MEDA" />
    </div>

    <h2>Welcome to MEDA Support</h2>

    <p>Please enter your name to continue</p>

    <input
      type="text"
      value={nameInput}
      placeholder="Enter your name"
      onChange={(e) => setNameInput(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          startChat();
        }
      }}
    />

    <button
      type="button"
      className="start-btn"
      onClick={startChat}
    >
      Start
    </button>

  </div>

) : (

<>
        {/* CHAT MESSAGES */}
        <div className="chat-messages">
          {chat.map((msg, i) => (
            <div key={i}>
             {msg.type === "greeting" || msg.type === "personal-greeting" ? (
                <div className="greeting-card">
                  <div className="greeting-avatar">
                    <img src="bot.png" alt="Bot" />
                  </div>
                  <div className="greeting-text">{msg.text}</div>
                </div>
              ) : msg.role === "bot" ? (
                <div className="bot-wrapper">
                  <img src="bot.png" alt="Bot" />
                  <div className="bot-msg">
                    {/* 🔔 REMARK — ALWAYS AT TOP */}
                    {msg.remark && (
                      <div className="remarks-box">{msg.remark}</div>
                    )}

                    {/* 📝 MAIN ANSWER (typed text) */}
                    <div className="answer-text">{msg.text}</div>

                    {/* TRANSLATE BUTTON */}
                    {msg.complete && !msg.translated && !msg.translating && (
                      <div
                        className="translate-btn"
                        onClick={() => translateMessage(msg.text, i)}
                      >
                        {isMarathi(msg.text)
                          ? "Translate to English"
                          : "Translate to Marathi"}
                      </div>
                    )}

                    {/* TRANSLATING LOADER */}
                    {msg.translating && (
                      <div className="translating">⏳ Translating...</div>
                    )}

                    {/* TRANSLATED TEXT */}
                    {msg.translated && (
                      <div className="bot-msg translated">{msg.translated}</div>
                    )}

                    {/* ⭐ SATISFACTION BUTTONS (YES / NO) */}
                    {/* {msg.complete && msg.askSatisfaction && !msg.skip_satisfaction && (
  <div className="satisfaction-box">
    <div
      className="satisfaction-btn yes"
      onClick={() => handleSatisfaction(i, true)}
    >
      Yes
    </div>
    <div
      className="satisfaction-btn no"
      onClick={() => handleSatisfaction(i, false)}
    >
      No
    </div>
  </div>
)} */}
                  </div>
                </div>
              ) : (
                <div className="user-wrapper">
                  <div className="user-msg">{msg.text}</div>
                  <img src="image.png" alt="User" />
                </div>
              )}
            </div>
          ))}

          {/* BOT IS TYPING */}
          {loading && (
            <div className="bot-wrapper">
              <img src="bot.png" alt="Bot" />
              <div className="bot-msg">Typing...</div>
            </div>
          )}

          <div ref={msgEndRef} />
        </div>

        {/* CATEGORY MENU */}
        {categories.length > 0 && !selectedCategory && (
          <div className="menu-box">
            <strong>Select Category:</strong>
            {categories.map((cat, i) => (
              <div
                key={i}
                className="scheme-bubble"
                onClick={() => fetchSchemes(cat)}
              >
                {cat}
              </div>
            ))}
          </div>
        )}

        {/* SCHEME MENU */}
        {schemes.length > 0 && !selectedScheme && (
          <div className="menu-box">
            <strong>Select Scheme:</strong>
            {schemes.map((sc, i) => (
              <div
                key={i}
                className="scheme-bubble-internal"
                onClick={() => fetchSchemeDetails(sc)}
              >
                {sc}
              </div>
            ))}

            <div
              className="back-button"
              onClick={() => {
                setSchemes([]);
                setSelectedCategory("");
              }}
            >
              ← Back
            </div>
          </div>
        )}

        <div className="chat-input">
          <input
            type="text"
            value={message}
            placeholder="Type your message..."
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            disabled={loading}
          />

          {/* 🎤 VOICE SEARCH BUTTON */}
          <button
            className={`voice-btn ${listening ? "active" : ""}`}
            onClick={() =>
              recognitionRef.current && recognitionRef.current.start()
            }
            disabled={listening || loading}
          >
            🎤
          </button>

          <button className="send-btn" onClick={sendMessage} disabled={loading}>
            Send
          </button>
        </div>
</>
)}
      </div>
    </>
  );
}

export default App;
